/* eslint-disable no-use-before-define */
import PropTypes from 'prop-types';
import * as APP from './App';

/*
** For nested PropTypes
*/
const lazyFunction = (f) => ((...args) => f().apply(this, args));

/*
** Global
*/
// Theme colors
export const themeColorsShape = PropTypes.shape({
  primary: PropTypes.string.isRequired,
  secondary: PropTypes.string.isRequired,
  error: PropTypes.string.isRequired,
  warning: PropTypes.string.isRequired,
  info: PropTypes.string.isRequired,
  success: PropTypes.string.isRequired,
});

// Country
export const countryShape = PropTypes.shape({
  code: PropTypes.string.isRequired,
  label: PropTypes.string,
});

// Menu
const menuItemShape = PropTypes.shape({
  iconId: PropTypes.string.isRequired,
  initiallyOpen: PropTypes.bool,
  key: PropTypes.string.isRequired,
  labelId: PropTypes.string.isRequired,
  paths: PropTypes.arrayOf(PropTypes.string),
  permissions: PropTypes.arrayOf(PropTypes.string),
  subItems: PropTypes.arrayOf(lazyFunction(() => menuItemShape)),
});
export const menuConfShape = PropTypes.shape({
  bottom: PropTypes.shape({
    labelId: PropTypes.string.isRequired,
    paths: PropTypes.arrayOf(PropTypes.string),
    items: PropTypes.arrayOf(menuItemShape).isRequired,
  }),
  header: PropTypes.shape({
    items: PropTypes.arrayOf(menuItemShape).isRequired,
  }),
  items: PropTypes.arrayOf(menuItemShape).isRequired,
});

// Menu items
export const menuItemsShape = PropTypes.arrayOf(
  PropTypes.shape({
    disabled: PropTypes.bool,
    handleAction: PropTypes.func,
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
    key: PropTypes.string.isRequired,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
    path: PropTypes.string,
    secondaryLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    selected: PropTypes.bool,
    visible: PropTypes.bool.isRequired,
  }),
);

// Error
export const errorShape = PropTypes.shape({
  errors: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array]),
  status: PropTypes.number,
});

// Notifications
export const notificationShape = PropTypes.shape({
  content: PropTypes.shape({
    template: PropTypes.string.isRequired,
    params: PropTypes.object,
  }),
  date: PropTypes.object.isRequired,
  dividers: PropTypes.bool,
  icon: PropTypes.string.isRequired,
  level: PropTypes.oneOf(['success', 'info', 'warning', 'error']).isRequired,
});

// Dialogs
export const dialogShape = PropTypes.shape({
  open: PropTypes.bool.isRequired,
  props: PropTypes.shape({
    title: PropTypes.shape({
      fixed: PropTypes.string,
      id: PropTypes.string,
      values: PropTypes.object,
    }),
    actions: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        preventClosing: PropTypes.bool,
        type: PropTypes.string.isRequired,
      }),
    ),
    component: PropTypes.shape({
      type: PropTypes.string.isRequired,
      props: PropTypes.object,
    }).isRequired,
    size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
    dividers: PropTypes.bool,
    confirmBeforeExit: PropTypes.bool,
  }),
});

// List row specs
export const listRowSpecsShape = PropTypes.shape({
  withSelection: PropTypes.bool.isRequired,
  selected: PropTypes.bool.isRequired,
  withChoose: PropTypes.bool.isRequired,
  chooseTooltipId: PropTypes.string,
  withActions: PropTypes.bool.isRequired,
  nbActions: PropTypes.number.isRequired,
  withDetails: PropTypes.bool.isRequired,
  showDetails: PropTypes.bool.isRequired,
  onShowHideDetails: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
});

// Lists
export const listShape = PropTypes.shape({
  data: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  })),
  pageNumber: PropTypes.number,
  pageSize: PropTypes.number,
  totalEntries: PropTypes.number,
  totalPages: PropTypes.number,
});
export const fullListShape = PropTypes.shape({
  desynchronized: PropTypes.bool,
  elements: listShape.isRequired,
  error: errorShape,
  loading: PropTypes.bool,
});
export const listFilterShape = PropTypes.shape({
  operator: PropTypes.oneOf(APP.FILTERS_OPERATORS).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.object]),
});

// List params
export const sortParamShape = PropTypes.shape({
  alias: PropTypes.string,
  dir: PropTypes.oneOf(['asc', 'desc']).isRequired,
  field: PropTypes.string.isRequired,
});
export const paginationParamShape = PropTypes.shape({
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
});
export const filterParamShape = PropTypes.shape({
  alias: PropTypes.string,
  field: PropTypes.string.isRequired,
  operator: PropTypes.oneOf(APP.FILTERS_OPERATORS).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
});
export const listParamShape = PropTypes.shape({
  filters: PropTypes.arrayOf(filterParamShape).isRequired,
  pagination: paginationParamShape.isRequired,
  sort: PropTypes.arrayOf(sortParamShape).isRequired,
});

// List features
// Default params
export const listFeatureDefaultParamShape = PropTypes.shape({
  pagination: paginationParamShape.isRequired,
  sort: PropTypes.arrayOf(sortParamShape).isRequired,
});
// Columns
export const listFeatureColumnShape = PropTypes.shape({
  alias: PropTypes.string,
  className: PropTypes.string,
  field: PropTypes.string.isRequired,
  hidden: PropTypes.bool,
  key: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  labelId: PropTypes.string,
  secondarySorts: PropTypes.arrayOf(sortParamShape),
  sortable: PropTypes.bool.isRequired,
});
// Search fields
const listFeatureSearchFieldTypeShape = PropTypes.shape({
  operators: PropTypes.arrayOf(PropTypes.string).isRequired,
  defaultOperator: PropTypes.func.isRequired,
  setValueTransform: PropTypes.func,
});
export const listFeatureSearchFieldShape = PropTypes.shape({
  excludeFilterFields: PropTypes.arrayOf(PropTypes.string),
  field: PropTypes.string.isRequired,
  filter: PropTypes.shape({
    alias: PropTypes.string,
    field: PropTypes.string.isRequired,
    key: PropTypes.string,
  }).isRequired,
  initial: PropTypes.any,
  isUndefined: PropTypes.func.isRequired,
  key: PropTypes.string.isRequired,
  labelId: PropTypes.string.isRequired,
  needGetOnInit: PropTypes.bool.isRequired,
  scope: PropTypes.string,
  type: listFeatureSearchFieldTypeShape.isRequired,
  withOperators: PropTypes.bool,
});
// Main
export const listFeatureShape = PropTypes.shape({
  columns: PropTypes.arrayOf(listFeatureColumnShape).isRequired,
  columnsQuantity: PropTypes.func,
  defaultParams: listFeatureDefaultParamShape.isRequired,
  searchFields: PropTypes.arrayOf(listFeatureSearchFieldShape).isRequired,
});

// AutoComplete
export const autoCompleteDataShape = PropTypes.shape({
  elements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    }).isRequired,
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  pagination: PropTypes.shape({
    pageNumber: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
  }),
});

// Entity manager
export const entityManagerShape = PropTypes.shape({
  currentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleChange: PropTypes.func.isRequired,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
});

/*
** Operations
*/

// Async operation
export const asyncOperationDeviceDataShape = PropTypes.shape({
  code: PropTypes.number.isRequired,
  desc: PropTypes.string,
  device_id: PropTypes.number.isRequired,
  ignored: PropTypes.bool.isRequired,
  status: PropTypes.oneOf([APP.ASYNC_OP_STATUS_PENDING, APP.ASYNC_OP_STATUS_SUCCESS, APP.ASYNC_OP_STATUS_ERROR]),
});
export const asyncOperationDataShape = PropTypes.shape({
  devices: PropTypes.arrayOf(asyncOperationDeviceDataShape).isRequired,
  id: PropTypes.number.isRequired,
  location: PropTypes.string,
  status: PropTypes.oneOf([APP.ASYNC_OP_STATUS_PENDING, APP.ASYNC_OP_STATUS_SUCCESS, APP.ASYNC_OP_STATUS_ERROR]).isRequired,
});

// Global
export const userOperationShape = PropTypes.shape({
  done: PropTypes.bool,
  error: errorShape,
});
export const addShape = PropTypes.shape({
  added: PropTypes.bool,
  asyncOperation: asyncOperationDataShape,
  error: errorShape,
});
export const terminateShape = PropTypes.shape({
  error: errorShape,
  terminated: PropTypes.bool,
});
export const updateShape = PropTypes.shape({
  asyncOperation: asyncOperationDataShape,
  error: errorShape,
  updated: PropTypes.bool,
});
export const replaceShape = PropTypes.shape({
  error: errorShape,
  replaced: PropTypes.bool,
});
export const sentShape = PropTypes.shape({
  error: errorShape,
  sent: PropTypes.bool,
});
export const deleteShape = PropTypes.shape({
  asyncOperation: asyncOperationDataShape,
  deleted: PropTypes.bool,
  error: errorShape,
});
export const importShape = PropTypes.shape({
  error: errorShape,
  loading: PropTypes.bool.isRequired,
  result: PropTypes.any,
});
export const triggerShape = PropTypes.shape({
  asyncOperation: asyncOperationDataShape,
  done: PropTypes.bool,
  error: errorShape,
});
export const sendCmdShape = PropTypes.shape({
  asyncOperation: asyncOperationDataShape,
  done: PropTypes.bool,
  error: errorShape,
});
export const cancelShape = PropTypes.shape({
  asyncOperation: asyncOperationDataShape,
  cancelled: PropTypes.bool,
  error: errorShape,
});

/*
** Permissions
*/
export const permissionsShape = PropTypes.shape({
  needed: PropTypes.string.isRequired,
  permissionsOrContext: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
});
export const permissionDataShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  product: PropTypes.oneOf(['opac', 'car_park_management']).isRequired,
});
export const permissionsListShape = PropTypes.shape({
  elements: PropTypes.arrayOf(permissionDataShape),
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

/*
** Auth
*/
export const authShape = PropTypes.shape({
  error: errorShape,
  loading: PropTypes.bool.isRequired,
  loggedIn: PropTypes.bool.isRequired,
  recoverPassword: PropTypes.shape({
    post: PropTypes.bool.isRequired,
    getLink: PropTypes.bool.isRequired,
    updateLink: PropTypes.bool.isRequired,
  }).isRequired,
  token: PropTypes.string,
});

/*
** Address & location
*/
const addressShape = PropTypes.shape({
  city: PropTypes.string,
  country: PropTypes.string,
  extra: PropTypes.string,
  street: PropTypes.string,
  zip_code: PropTypes.string,
});
export const locationShape = PropTypes.shape({
  latitude: PropTypes.number,
  longitude: PropTypes.number,
});

/*
** Operator
*/
export const operatorDataShape = PropTypes.shape({
  code: PropTypes.string.isRequired,
  country: PropTypes.string,
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
});

// ---------------------------------------------------------- //
// -------------------- Modelized shapes -------------------- //
// ---------------------------------------------------------- //

/*
** Alertings
*/
export const alertingDataShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  medias: PropTypes.arrayOf(PropTypes.string),
  type: PropTypes.string.isRequired,
});
export const alertingsShape = PropTypes.shape({
  elements: PropTypes.arrayOf(alertingDataShape),
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

/*
** Apns
*/
export const apnDataShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  identifier: PropTypes.string.isRequired,
  login: PropTypes.string,
  name: PropTypes.string.isRequired,
  password: PropTypes.string,
});

/*
** Companies
*/
export const companyDataShape = PropTypes.shape({
  children: PropTypes.arrayOf(lazyFunction(() => companyDataShape)),
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
});
export const currentCompanyShape = PropTypes.shape({
  data: companyDataShape,
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});
export const companiesShape = PropTypes.shape({
  elements: PropTypes.arrayOf(companyDataShape),
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

/*
** Customers
*/
export const customerAccessWayRightDataShape = PropTypes.shape({
  begin: PropTypes.string,
  enabled: PropTypes.bool.isRequired,
  end: PropTypes.string,
  id: PropTypes.number,
  planning_id: PropTypes.number,
  scenario_trigger_id: PropTypes.number.isRequired,
});
export const customerAccessWayDataShape = PropTypes.shape({
  desc: PropTypes.string.isRequired,
  enabled: PropTypes.bool.isRequired,
  id: PropTypes.number,
  rights: PropTypes.arrayOf(customerAccessWayRightDataShape),
  type: PropTypes.oneOf(['phone', 'keypad_code', 'license_plate']).isRequired,
});
export const customerDataShape = PropTypes.shape({
  access_ways: PropTypes.arrayOf(customerAccessWayDataShape),
  async_operation_id: PropTypes.number,
  comments: PropTypes.string,
  email: PropTypes.string,
  entity_id: PropTypes.number.isRequired,
  firstname: PropTypes.string,
  id: PropTypes.number.isRequired,
  is_synchronized: PropTypes.bool.isRequired,
  last_access: PropTypes.string,
  lastname: PropTypes.string.isRequired,
  metadata: PropTypes.shape({
    booking_id: PropTypes.string,
    booking_usage: PropTypes.oneOf(['b2b_subscription', 'package', 'subscription']),
  }),
  restricted_by_antipassback_entrance: PropTypes.bool.isRequired,
  restricted_by_antipassback_exit: PropTypes.bool.isRequired,
  state: PropTypes.shape({
    date: PropTypes.string.isRequired,
    delta: PropTypes.number,
    inside_quantity: PropTypes.number.isRequired,
    is_computed: PropTypes.bool,
    outside_quantity: PropTypes.number.isRequired,
    unknown_quantity: PropTypes.number.isRequired,
  }).isRequired,
  sync_status: PropTypes.object,
  weight: PropTypes.number,
});
export const currentCustomerShape = PropTypes.shape({
  data: customerDataShape,
  error: errorShape,
});

/*
** Distributions
*/
const distributionFileDataShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  size: PropTypes.number.isRequired,
  uploaded: PropTypes.bool,
});
export const distributionDataShape = PropTypes.shape({
  comment: PropTypes.string,
  date: PropTypes.string.isRequired,
  devices_quantity: PropTypes.number,
  files: PropTypes.arrayOf(distributionFileDataShape),
  id: PropTypes.number.isRequired,
  rollouts_quantity: PropTypes.number,
  type: PropTypes.string.isRequired,
  version: PropTypes.string.isRequired,
});
export const currentDistributionShape = PropTypes.shape({
  data: distributionDataShape,
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

/**
* Devices
*/
const deviceConfigDataShape = PropTypes.shape({
  async_operation_id: PropTypes.number,
  default_operator_id: PropTypes.number,
  equipments_network_ip: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  is_synchronized: PropTypes.bool.isRequired,
  log_level: PropTypes.string.isRequired,
  ignore_plate_duration: PropTypes.number.isRequired,
  monitoring_timer: PropTypes.number.isRequired,
  tcp_port: PropTypes.number.isRequired,
  udp_port: PropTypes.number.isRequired,
  operators_blacklist: PropTypes.arrayOf(PropTypes.number).isRequired,
  operators_whitelist_countries: PropTypes.arrayOf(PropTypes.string).isRequired,
  periodic_synchronization_timer: PropTypes.number.isRequired,
  server_url: PropTypes.string.isRequired,
  sync_status: PropTypes.object,
});
export const deviceModelDataShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string,
  productible: PropTypes.bool.isRequired,
  type: PropTypes.string.isRequired,
  version: PropTypes.string.isRequired,
});
export const deviceModelsShape = PropTypes.shape({
  elements: PropTypes.arrayOf(deviceModelDataShape),
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});
export const deviceSlaveRelayDataShape = PropTypes.shape({
  code: PropTypes.oneOf(['r1', 'r2']).isRequired,
  doors: PropTypes.arrayOf(PropTypes.number),
  id: PropTypes.number.isRequired,
});
export const deviceSlaveInputDataShape = PropTypes.shape({
  code: PropTypes.oneOf(['cs1', 'cs2', 'an']).isRequired,
  id: PropTypes.number.isRequired,
  sources: PropTypes.arrayOf(PropTypes.number),
});
export const deviceSlaveTriggerDataShape = PropTypes.shape({
  equipment: equipmentDataShape,
  id: PropTypes.number,
  protocols: PropTypes.arrayOf(PropTypes.oneOf(['wiegand', 'dataclock', 'rs232', 'ethernet'])),
  type: PropTypes.oneOf(['api', 'gsm', 'e1', 'e2', 'serial1', 'serial2', 'ethernet']).isRequired,
});
export const deviceSlaveDataShape = PropTypes.shape({
  async_operation_id: PropTypes.number,
  company_id: PropTypes.number,
  device: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }),
  device_id: PropTypes.number,
  device_model: deviceModelDataShape.isRequired,
  device_production_batch_id: PropTypes.number,
  id: PropTypes.number.isRequired,
  inputs: PropTypes.arrayOf(deviceSlaveInputDataShape),
  is_synchronized: PropTypes.bool.isRequired,
  name: PropTypes.string,
  relays: PropTypes.arrayOf(deviceSlaveRelayDataShape),
  serial: PropTypes.string.isRequired,
  sync_status: PropTypes.object,
  triggers: PropTypes.arrayOf(deviceSlaveTriggerDataShape),
  virtual_trigger_types: PropTypes.arrayOf(PropTypes.string),
});
export const currentDeviceSlaveShape = PropTypes.shape({
  data: deviceSlaveDataShape,
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});
export const deviceInfoDataShape = PropTypes.shape({
  modem_imei: PropTypes.string,
  modem_manufacturer: PropTypes.string,
  modem_model: PropTypes.string,
  modem_revision: PropTypes.string,
  network_access_technology: PropTypes.string,
  network_level: PropTypes.number,
  network_operator: operatorDataShape,
});
export const deviceSimcardHistoryDataShape = PropTypes.shape({
  begin: PropTypes.string.isRequired,
  end: PropTypes.string.isRequired,
  iccid: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  number: PropTypes.string.isRequired,
});
export const deviceDataShape = PropTypes.shape({
  company_id: PropTypes.number,
  config: deviceConfigDataShape,
  device_distribution_maps: PropTypes.arrayOf(distributionDataShape),
  device_model: deviceModelDataShape,
  device_production_batch_id: PropTypes.number,
  device_slaves: PropTypes.arrayOf(deviceSlaveDataShape),
  disabled: PropTypes.bool,
  id: PropTypes.number.isRequired,
  info: deviceInfoDataShape,
  installation: PropTypes.shape({
    device: PropTypes.shape({
      disabled: PropTypes.bool.isRequired,
      name: PropTypes.string.isRequired,
      since: PropTypes.string.isRequired,
    }).isRequired,
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }),
  ip_address: PropTypes.string,
  last_comm_date: PropTypes.string,
  name: PropTypes.string,
  rollout_in_progress: PropTypes.bool,
  serial: PropTypes.string,
  simcard: simcardDataShape,
  simcard_history: PropTypes.arrayOf(deviceSimcardHistoryDataShape),
  sync_loading: PropTypes.bool,
  sync_status: PropTypes.shape({
    antipassbacks: PropTypes.bool,
    apns: PropTypes.bool.isRequired,
    config: PropTypes.bool.isRequired,
    customers: PropTypes.bool.isRequired,
    entities: PropTypes.bool,
    installations: PropTypes.bool,
    plannings: PropTypes.bool.isRequired,
    scenarios: PropTypes.bool.isRequired,
    slaves: PropTypes.bool.isRequired,
    tasks: PropTypes.bool,
  }),
  timeout: PropTypes.bool,
});
export const currentDeviceShape = PropTypes.shape({
  data: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }),
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});
export const devicesShape = PropTypes.shape({
  elements: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
  })),
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});
export const deviceDistributionDataShape = PropTypes.shape({
  begin: PropTypes.string.isRequired,
  distribution: distributionDataShape,
  end: PropTypes.string,
  id: PropTypes.number.isRequired,
});
export const deviceDistributionsListShape = PropTypes.shape({
  elements: PropTypes.arrayOf(deviceDistributionDataShape),
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});
export const deviceSmsDataShape = PropTypes.shape({
  date: PropTypes.string.isRequired,
  delivery_status: PropTypes.oneOf(['pending', 'delivered', 'error']).isRequired,
  id: PropTypes.number.isRequired,
  type: PropTypes.oneOf(['sync', 'reset', 'reboot', 'status']).isRequired,
});
export const deviceSmsListShape = PropTypes.shape({
  elements: PropTypes.arrayOf(deviceSmsDataShape),
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});
export const deviceBatchDataShape = PropTypes.shape({
  author_id: PropTypes.number,
  commit_date: PropTypes.string,
  committer_id: PropTypes.number,
  date: PropTypes.string.isRequired,
  device_model_id: PropTypes.number.isRequired,
  devices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      serial: PropTypes.string.isRequired,
    }),
  ),
  distributions: PropTypes.object.isRequired,
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['pending', 'committed']).isRequired,
});
export const currentDeviceBatchShape = PropTypes.shape({
  data: deviceBatchDataShape,
  error: errorShape,
});
export const deviceLogDataShape = PropTypes.shape({
  date: PropTypes.string.isRequired,
  desc: PropTypes.string,
  id: PropTypes.number.isRequired,
  level: PropTypes.oneOf(['debug', 'info', 'warning', 'error']).isRequired,
  origin: PropTypes.oneOf(['device', 'server']).isRequired,
  tag: PropTypes.oneOf(['connectivity', 'system', 'power', 'idle']).isRequired,
  type: PropTypes.string.isRequired,
});
export const deviceCmdDataShape = PropTypes.shape({
  asyncOperationId: PropTypes.number.isRequired,
  device: PropTypes.shape({
    id: PropTypes.number.isRequired,
    serial: PropTypes.string.isRequired,
  }).isRequired,
  cmd: PropTypes.oneOf(['ping', 'sync', 'reboot', 'reset']).isRequired,
});
export const deviceEventDataShape = PropTypes.shape({
  data: PropTypes.object.isRequired,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  status: PropTypes.oneOf(['pending', 'done']).isRequired,
  type: PropTypes.oneOf(['deviceInfo', 'deviceCmd', 'deviceSms', 'scenarioTrigger', 'rightTrigger']).isRequired,
});

/*
** Doors
*/
export const doorDataShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  relays: PropTypes.arrayOf(PropTypes.number),
  scenario: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }),
  scenario_id: PropTypes.number,
  type: PropTypes.oneOf(['door', 'fence', 'gate']).isRequired,
  usage: PropTypes.oneOf(['vehicle', 'pedestrian']).isRequired,
});
export const currentDoorShape = PropTypes.shape({
  data: doorDataShape,
  error: errorShape,
});
export const doorsShape = PropTypes.shape({
  elements: PropTypes.arrayOf(doorDataShape),
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

/*
** Entities
*/
export const entityDataShape = PropTypes.shape({
  async_operation_id: PropTypes.number,
  capacity: PropTypes.number,
  company_id: PropTypes.number.isRequired,
  id: PropTypes.number.isRequired,
  is_synchronized: PropTypes.bool.isRequired,
  name: PropTypes.string,
  product: PropTypes.oneOf(['opac', 'car_park_management']).isRequired,
  sync_status: PropTypes.object,
  type: PropTypes.oneOf(['master', 'hosts_onepark_bookings', 'regular']).isRequired,
});
export const entitiesShape = PropTypes.shape({
  elements: PropTypes.arrayOf(entityDataShape),
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

/*
** Equipment
*/
export const equipmentDataShape = PropTypes.shape({
  brand: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  ip: PropTypes.string,
  mac: PropTypes.string,
  model: PropTypes.string.isRequired,
  name: PropTypes.string,
  protocol: PropTypes.oneOf(['wiegand', 'dataclock', 'rs232', 'ethernet']).isRequired,
  status: PropTypes.oneOf(['ok', 'nok']),
  type: PropTypes.oneOf(['keypad', 'license_plate_reader', 'rfid_reader', 'uhf_reader']).isRequired,
});

/*
** Installations
*/
export const installationAsyncOperationDeviceDataShape = PropTypes.shape({
  async_date: PropTypes.string,
  async_status: PropTypes.oneOf(['ok', 'pending']).isRequired,
  device_id: PropTypes.number.isRequired,
  sync_error_code: PropTypes.number,
  sync_error_desc: PropTypes.string,
  sync_error_ignored: PropTypes.bool,
  sync_status: PropTypes.oneOf(['success', 'pending', 'error', 'na', 'aborted']),
});
export const installationAsyncOperationDataShape = PropTypes.shape({
  author_id: PropTypes.number,
  date: PropTypes.string.isRequired,
  devices: PropTypes.arrayOf(installationAsyncOperationDeviceDataShape).isRequired,
  id: PropTypes.number.isRequired,
  method: PropTypes.oneOf(['get', 'post', 'put', 'delete']),
  resource_id: PropTypes.number.isRequired,
  resource_info: PropTypes.string,
  resource_type: PropTypes.oneOf([
    'allotment', 'access_way', 'antipassback', 'customer', 'device',
    'device_apn_map', 'device_config', 'device_slave', 'entity', 'installation',
    'parking', 'parking_category', 'planning', 'price_grid', 'right', 'rollout',
    'scenario', 'task',
  ]).isRequired,
  rollback_status: PropTypes.oneOf(['committed', 'pending', 'rollbacked']).isRequired,
  status: PropTypes.oneOf([APP.ASYNC_OP_STATUS_PENDING, APP.ASYNC_OP_STATUS_SUCCESS, APP.ASYNC_OP_STATUS_ERROR]).isRequired,
});

export const installationDataShape = PropTypes.shape({
  address: addressShape,
  antipassback: PropTypes.shape({
    enabled_entrance: PropTypes.bool.isRequired,
    enabled_exit: PropTypes.bool.isRequired,
    required_entrance: PropTypes.bool.isRequired,
    required_exit: PropTypes.bool.isRequired,
  }),
  async_operation_id: PropTypes.number,
  async_operations_error_quantity: PropTypes.number,
  async_operations_success_rate: PropTypes.number,
  company_id: PropTypes.number,
  devices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  ),
  entities: PropTypes.arrayOf(entityDataShape),
  id: PropTypes.number.isRequired,
  inserted_at: PropTypes.string,
  is_synchronized: PropTypes.bool,
  last_error_async_operation: installationAsyncOperationDataShape,
  last_success_async_operation: installationAsyncOperationDataShape,
  location: locationShape,
  name: PropTypes.string.isRequired,
  production: PropTypes.bool,
  sync_status: PropTypes.object,
});
export const currentInstallationShape = PropTypes.shape({
  error: errorShape,
  loading: PropTypes.bool.isRequired,
  data: installationDataShape,
});

export const installationLogScenarioLogActionDataShape = PropTypes.shape({
  delay: PropTypes.number.isRequired,
  expression: PropTypes.string.isRequired,
  next: lazyFunction(() => installationLogScenarioLogActionDataShape),
  success: PropTypes.bool.isRequired,
  time: PropTypes.obj,
});
export const installationLogScenarioLogDataShape = PropTypes.shape({
  date: PropTypes.string.isRequired,
  exec: PropTypes.shape({
    actions: PropTypes.arrayOf(installationLogScenarioLogActionDataShape).isRequired,
    conflicts: PropTypes.obj,
    input_condition: PropTypes.string,
    input_trigger: PropTypes.string,
    priority: PropTypes.string,
  }).isRequired,
  id: PropTypes.number.isRequired,
  scenario_id: PropTypes.number.isRequired,
  status: PropTypes.oneOf(['success', 'error_input_contition', 'error_self_exclusion', 'error']).isRequired,
  success_condition: PropTypes.oneOf(['and', 'or', 'none']).isRequired,
});
const installationLogScenarioLogInactiveDataShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  scenario_name: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['success', 'error_input_contition', 'error_self_exclusion', 'error']).isRequired,
});
export const installationLogDataShape = PropTypes.shape({
  access_way_id: PropTypes.number,
  customer: PropTypes.shape({
    lastname: PropTypes.string.isRequired,
    firstname: PropTypes.string,
    id: PropTypes.number.isRequired,
  }),
  date: PropTypes.string.isRequired,
  desc: PropTypes.string,
  read_desc: PropTypes.string,
  device_id: PropTypes.number,
  id: PropTypes.number.isRequired,
  right_id: PropTypes.number,
  scenario_log: PropTypes.oneOfType([installationLogScenarioLogDataShape, installationLogScenarioLogInactiveDataShape]),
  scenario_trigger_type: PropTypes.oneOf(['input', 'output', 'na']),
  task: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }),
  trigger: PropTypes.shape({
    equipment: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }),
    id: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
  }),
  type: PropTypes.oneOf(['access', 'api', 'api_right', 'task', 'trigger']).isRequired,
  value: PropTypes.oneOf([
    'access_way_disabled',
    'allotment_and_entity_capacity',
    'forbidden_entry_during_exit_courtesy_delay',
    'allotment_planning',
    'anonymous_call',
    'antipassback_state',
    'disabled',
    'no_right',
    'ok',
    'permanent_exit',
    'right_date',
    'right_disabled',
    'right_planning',
    'scenario_trigger_disabled',
    'unknown_access_way',
    'success_drop_off',
    'drop_off',
  ]).isRequired,
});

/*
** Plannings
*/
export const planningDataShape = PropTypes.shape({
  async_operation_id: PropTypes.number,
  enabled: PropTypes.bool.isRequired,
  entity_id: PropTypes.number.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  is_synchronized: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  slots: PropTypes.arrayOf(PropTypes.shape({
    end: PropTypes.shape({
      day_of_week: PropTypes.oneOf(['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su']).isRequired,
      time: PropTypes.string.isRequired,
    }).isRequired,
    start: PropTypes.shape({
      day_of_week: PropTypes.oneOf(['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su']).isRequired,
      time: PropTypes.string.isRequired,
    }).isRequired,
  })),
  sync_status: PropTypes.object,
});
export const currentPlanningShape = PropTypes.shape({
  data: planningDataShape,
  error: errorShape,
});
export const planningsShape = PropTypes.shape({
  elements: PropTypes.arrayOf(planningDataShape),
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

/*
** Profiles
*/
export const profileDataShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
  product: PropTypes.oneOf(['opac', 'car_park_management']).isRequired,
});
export const currentProfileShape = PropTypes.shape({
  data: profileDataShape,
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

/*
** Rollouts
*/
export const rolloutDataShape = PropTypes.shape({
  date: PropTypes.string.isRequired,
  devices_quantities: PropTypes.shape({
    cancelled: PropTypes.number.isRequired,
    error: PropTypes.number.isRequired,
    in_progress: PropTypes.number.isRequired,
    pending: PropTypes.number.isRequired,
    success: PropTypes.number.isRequired,
  }).isRequired,
  distribution: distributionDataShape,
  force_complete_update: PropTypes.bool.isRequired,
  id: PropTypes.number.isRequired,
  statuses: PropTypes.object,
});
export const currentRolloutShape = PropTypes.shape({
  data: rolloutDataShape,
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});
export const rolloutDeviceDataShape = PropTypes.shape({
  device: PropTypes.shape({
    id: PropTypes.number.isRequired,
    serial: PropTypes.string.isRequired,
    timeout: PropTypes.bool.isRequired,
  }),
  id: PropTypes.number.isRequired,
  statuses: PropTypes.object,
});

/*
** Scenarios
*/
export const scenarioActionDataShape = PropTypes.shape({
  delay: PropTypes.number.isRequired,
  duration: PropTypes.number.isRequired,
  expression: PropTypes.string.isRequired,
  id: PropTypes.number,
  input_condition: scenarioInputDataShape,
  input_trigger: scenarioInputDataShape,
  next: lazyFunction(() => scenarioActionDataShape),
});
const scenarioInputDataShape = PropTypes.shape({
  expression: PropTypes.string.isRequired,
  id: PropTypes.number,
});
export const scenarioTriggerDataShape = PropTypes.shape({
  enabled: PropTypes.bool.isRequired,
  id: PropTypes.number,
  on_demand_enabled: PropTypes.bool.isRequired,
  on_demand_validation_trigger_id: PropTypes.number,
  trigger_id: PropTypes.number.isRequired,
  type: PropTypes.oneOf(['input', 'output', 'na']).isRequired,
});
export const scenarioDataShape = PropTypes.shape({
  actions: PropTypes.arrayOf(scenarioActionDataShape),
  async_operation_id: PropTypes.number,
  device_id: PropTypes.number.isRequired,
  id: PropTypes.number.isRequired,
  input_condition: scenarioInputDataShape,
  input_trigger: scenarioInputDataShape,
  involved_in_antipassback: PropTypes.bool.isRequired,
  is_synchronized: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  priority: PropTypes.oneOf(['low', 'normal', 'high']).isRequired,
  scenario_triggers: PropTypes.arrayOf(scenarioTriggerDataShape),
  self_exclusion: PropTypes.bool.isRequired,
  success_condition: PropTypes.oneOf(['and', 'or', 'none']).isRequired,
  sync_status: PropTypes.object,
});
export const currentScenarioShape = PropTypes.shape({
  data: scenarioDataShape,
  error: errorShape,
});
export const scenariosShape = PropTypes.shape({
  elements: PropTypes.arrayOf(scenarioDataShape),
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

/*
** Simcards
*/
export const simcardDeviceHistoryDataShape = PropTypes.shape({
  begin: PropTypes.string.isRequired,
  end: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  serial: PropTypes.string.isRequired,
});
export const simcardGroupDataShape = PropTypes.shape({
  apns: PropTypes.arrayOf(PropTypes.number),
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
});
export const simcardDataShape = PropTypes.shape({
  device: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }),
  device_history: PropTypes.arrayOf(simcardDeviceHistoryDataShape),
  group: simcardGroupDataShape,
  group_id: PropTypes.number,
  iccid: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  number: PropTypes.string,
  status: PropTypes.oneOf(['unknown', 'activation_in_progress', 'active', 'inactive', 'suspended']).isRequired,
});
export const currentSimcardShape = PropTypes.shape({
  data: simcardDataShape,
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

/*
** Sources
*/
export const sourceDataShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  inputs: PropTypes.arrayOf(PropTypes.number),
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['pushbutton', 'induction_loop', 'sensor']).isRequired,
});
export const currentSourceShape = PropTypes.shape({
  data: sourceDataShape,
  error: errorShape,
});
export const sourcesShape = PropTypes.shape({
  elements: PropTypes.arrayOf(sourceDataShape),
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

/*
** Tasks
*/
export const taskDataShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
});

/*
** Users
*/
export const userCallbackDataShape = PropTypes.shape({
  subscriptions: PropTypes.arrayOf(
    PropTypes.oneOf([
      'antipassback_create', 'antipassback_update', 'antipassback_delete', 'antipassback_head',
      'apn_create', 'apn_update', 'apn_delete', 'apn_head',
      'async_operation',
      'device_config_create', 'device_config_update', 'device_config_delete', 'device_config_head',
      'customer_create', 'customer_update', 'customer_delete', 'customer_head',
      'customer_antipassback_create', 'customer_antipassback_update', 'customer_antipassback_delete', 'customer_antipassback_head',
      'device_log',
      'installation_create', 'installation_update', 'installation_delete', 'installation_head',
      'entity_create', 'entity_update', 'entity_delete', 'entity_head',
      'installation_log',
      'planning_create', 'planning_update', 'planning_delete', 'planning_head',
      'scenario_create', 'scenario_update', 'scenario_delete', 'scenario_head',
      'slave_create', 'slave_update', 'slave_delete', 'slave_head',
      'task_create', 'task_update', 'task_delete', 'task_head',
    ]),
  ),
  uri: PropTypes.string.isRequired,
  version: PropTypes.string.isRequired,
});
export const userAlertingDataShape = PropTypes.shape({
  alerting_id: PropTypes.number.isRequired,
  config: PropTypes.object,
  media: PropTypes.string.isRequired,
});
export const userPermissionsDataShape = PropTypes.shape({
  company_id: PropTypes.number,
  permissions: PropTypes.arrayOf(PropTypes.string),
  profile_id: PropTypes.number,
});
export const userDataShape = PropTypes.shape({
  callback: userCallbackDataShape,
  comment: PropTypes.string,
  email: PropTypes.string,
  firstname: PropTypes.string,
  id: PropTypes.number.isRequired,
  lastname: PropTypes.string.isRequired,
  password: PropTypes.string,
  permissions: PropTypes.arrayOf(userPermissionsDataShape),
  phone: PropTypes.string,
  public_id: PropTypes.string,
  user_alertings: PropTypes.arrayOf(userAlertingDataShape),
  rollback_strategy: PropTypes.oneOf(['always_commit', 'rollback']),
});
export const currentUserShape = PropTypes.shape({
  data: userDataShape,
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

/*
** Me
*/
export const meDataShape = PropTypes.shape({
  alertings: PropTypes.arrayOf(userAlertingDataShape),
  callback: userCallbackDataShape,
  email: PropTypes.string,
  firstname: PropTypes.string,
  id: PropTypes.number.isRequired,
  lastname: PropTypes.string.isRequired,
  phone: PropTypes.string,
  public_id: PropTypes.string,
});
export const meShape = PropTypes.shape({
  data: meDataShape,
  error: errorShape,
  loading: PropTypes.bool.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string),
  update: updateShape.isRequired,
});

// ---------------------------------------------------------- //
// --------------- Car park management shapes --------------- //
// ---------------------------------------------------------- //

/*
** Device
*/
export const cpmDeviceDataShape = PropTypes.shape({
  disabled: PropTypes.bool,
  id: PropTypes.number.isRequired,
  name: PropTypes.string,
  serial: PropTypes.string,
  timeout: PropTypes.bool,
  lastCommDate: PropTypes.string,
});

/**
* Operations
*/
const cpmOperationDeviceErrorSchape = PropTypes.shape({
  code: PropTypes.number.isRequired,
  desc: PropTypes.string,
  ignored: PropTypes.bool.isRequired,
});
const cpmOperationDeviceShape = PropTypes.shape({
  device: cpmDeviceDataShape.isRequired,
  error: cpmOperationDeviceErrorSchape,
  status: PropTypes.oneOf([APP.ASYNC_OP_STATUS_PENDING, APP.ASYNC_OP_STATUS_SUCCESS, APP.ASYNC_OP_STATUS_ERROR]).isRequired,
});
export const cpmOperationDataShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  devices: PropTypes.arrayOf(cpmOperationDeviceShape).isRequired,
  status: PropTypes.oneOf([APP.ASYNC_OP_STATUS_PENDING, APP.ASYNC_OP_STATUS_SUCCESS, APP.ASYNC_OP_STATUS_ERROR]).isRequired,
});

/**
* Events
*/
export const cpmEventDataShape = PropTypes.shape({
  data: PropTypes.object.isRequired,
  id: PropTypes.number.isRequired,
  status: PropTypes.oneOf(['pending', 'done']).isRequired,
  type: PropTypes.oneOf(['doorTrigger']).isRequired,
});

/*
** Synchronization
*/
const cpmSynchronizationStatusDeviceShape = PropTypes.shape({
  device: cpmDeviceDataShape.isRequired,
  version: PropTypes.number.isRequired,
});
export const cpmSynchronizationStatusShape = PropTypes.shape({
  devices: PropTypes.arrayOf(cpmSynchronizationStatusDeviceShape).isRequired,
  version: PropTypes.number.isRequired,
});

/*
** Door
*/
const cpmEquipmentDataShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string,
  type: PropTypes.oneOf(['keypad', 'license_plate_reader']),
  cameraStatus: PropTypes.oneOf(['nok', 'ok', '']),
});
const cpmDoorEquipmentDataShape = PropTypes.shape({
  equipment: cpmEquipmentDataShape.isRequired,
  usage: PropTypes.oneOf(['input', 'output', 'na']).isRequired,
});
export const cpmDoorDataShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  equipments: PropTypes.arrayOf(cpmDoorEquipmentDataShape),
  device: cpmDeviceDataShape,
  name: PropTypes.string,
  type: PropTypes.oneOf(['door', 'fence', 'gate']),
  usage: PropTypes.oneOf(['vehicle', 'pedestrian']),
});

/*
** Price
*/
export const cpmPriceRuleDataShape = PropTypes.shape({
  country: PropTypes.string,
  currency: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  taxRate: PropTypes.number.isRequired,
});

/*
** Car parks
*/
export const cpmCarparkSettingsOnDemandDataShape = PropTypes.shape({
  configuration: PropTypes.shape({
    parkingCategory: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
    exitCourtesyDelay: PropTypes.number,
    resetDelay: PropTypes.number,
    planning: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
    priceGrid: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
    quickParkingCategory: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
    reservedCapacity: PropTypes.number,
  }),
  enabled: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['auto_categorization', 'specific_category']),
});
export const cpmCarparkSettingsSpacesDataShape = PropTypes.shape({
  bookingCapacity: PropTypes.number.isRequired,
  assignedCapacity: PropTypes.number.isRequired,
});

export const cpmCarparkDataPriceRulesOnlyShape = PropTypes.shape({
  data: PropTypes.shape({
    priceRules: PropTypes.arrayOf(cpmPriceRuleDataShape),
  }),
});

export const cpmCarparkDataShape = PropTypes.shape({
  address: addressShape,
  capacity: PropTypes.number,
  devices: PropTypes.arrayOf(cpmDeviceDataShape),
  doors: PropTypes.arrayOf(cpmDoorDataShape),
  id: PropTypes.number,
  location: locationShape,
  name: PropTypes.string,
  settings: PropTypes.shape({
    onDemand: cpmCarparkSettingsOnDemandDataShape,
  }),
  terminals: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    appStatus: PropTypes.string,
    appVersion: PropTypes.string,
    batteryLevel: PropTypes.number,
    lastCommDate: PropTypes.string,
    paymentReaders: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      chargingStatus: PropTypes.string,
      connectionStatus: PropTypes.string,
      firmwareVersion: PropTypes.string,
      serialNumber: PropTypes.string,
    })),
    serialNumber: PropTypes.string,
    timeout: PropTypes.bool,
    type: PropTypes.string,
  })),
  slug: PropTypes.shape({
    id: PropTypes.number.isRequired,
    domain: PropTypes.string.isRequired,
    slug: PropTypes.string,
  }),
  timezone: PropTypes.string,
});

export const cpmCarparkSynchronizationStatusesDataShape = PropTypes.shape({
  bookings: PropTypes.bool.isRequired,
  onDemandAccesses: PropTypes.bool.isRequired,
  parkings: PropTypes.bool.isRequired,
  parkingCategories: PropTypes.bool.isRequired,
  plannings: PropTypes.bool.isRequired,
});
export const cpmCarparkSynchronizationStatusesShape = PropTypes.shape({
  data: cpmCarparkSynchronizationStatusesDataShape,
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});
export const cpmCurrentCarparkShape = PropTypes.shape({
  data: cpmCarparkDataShape,
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});
export const cpmCarparkPublicDataShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  onDemandExitCourtesyDelay: PropTypes.number,
  onDemandTemporaryExitMaxDuration: PropTypes.number,
  publicName: PropTypes.string,
  slug: cpmSlugShape,
});
/*
** Plannings
*/
export const cpmPlanningDataShape = PropTypes.shape({
  active: PropTypes.bool,
  enabled: PropTypes.bool.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  slots: PropTypes.arrayOf(PropTypes.shape({
    end: PropTypes.shape({
      dayOfWeek: PropTypes.oneOf(['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su']).isRequired,
      time: PropTypes.string.isRequired,
    }).isRequired,
    start: PropTypes.shape({
      dayOfWeek: PropTypes.oneOf(['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su']).isRequired,
      time: PropTypes.string.isRequired,
    }).isRequired,
  })),
  synchronizationStatus: cpmSynchronizationStatusShape,
});
export const cpmCurrentPlanningShape = PropTypes.shape({
  data: cpmPlanningDataShape,
  error: errorShape,
});
export const cpmPlanningsShape = PropTypes.shape({
  elements: PropTypes.arrayOf(cpmPlanningDataShape),
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

/*
** Parking categories
*/
export const cpmParkingCategoryDataShape = PropTypes.shape({
  color: PropTypes.string,
  id: PropTypes.number.isRequired,
  isOnepark: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  priceGridAssociation: PropTypes.shape({
    discount: PropTypes.number.isRequired,
    priceGrid: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }).isRequired,
  }),
  synchronizationStatus: cpmSynchronizationStatusShape,
  type: PropTypes.oneOf(['on_demand', 'regular']).isRequired,
});
export const cpmParkingCategoriesShape = PropTypes.shape({
  elements: PropTypes.arrayOf(cpmParkingCategoryDataShape),
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

/*
** Price grids
*/
export const cpmPriceGridDataShape = PropTypes.shape({
  additional: PropTypes.shape({
    amountInclTax: PropTypes.number.isRequired,
  }).isRequired,
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  parkingCategoriesAssociations: PropTypes.arrayOf(
    PropTypes.shape({
      discount: PropTypes.number.isRequired,
      calculationMode: PropTypes.string,
      parkingCategory: PropTypes.shape({
        id: PropTypes.number.isRequired,
      }).isRequired,
    }),
  ).isRequired,
  priceRule: cpmPriceRuleDataShape.isRequired,
  tiers: PropTypes.arrayOf(
    PropTypes.shape({
      amountInclTax: PropTypes.number.isRequired,
      duration: PropTypes.number.isRequired,
      unit: PropTypes.oneOf(['day', 'hour', 'minute']).isRequired,
    }),
  ).isRequired,
});
export const cpmPriceGridsShape = PropTypes.shape({
  elements: PropTypes.arrayOf(cpmPriceGridDataShape),
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});
export const cpmBookingPriceGridDataShape = PropTypes.shape({
  additional: PropTypes.shape({
    amountInclTax: PropTypes.number.isRequired,
  }).isRequired,
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  priceRule: cpmPriceRuleDataShape.isRequired,
  tiers: PropTypes.arrayOf(
    PropTypes.shape({
      amountInclTax: PropTypes.number.isRequired,
      duration: PropTypes.number.isRequired,
      unit: PropTypes.oneOf(['day', 'hour', 'minute']).isRequired,
    }),
  ).isRequired,
});

/*
** Accesses
*/
export const cpmAccessDataShape = PropTypes.shape({
  date: PropTypes.string.isRequired,
  door: cpmDoorDataShape,
  equipment: cpmEquipmentDataShape,
  id: PropTypes.number.isRequired,
  parking: cpmParkingDataShape,
  result: PropTypes.oneOf([
    'error_antipassback',
    'error_capacity',
    'error_dates',
    'error_doors',
    'error_execution',
    'error_forbidden_exit',
    'error_forbidden_entry_during_exit_courtesy_delay',
    'error_permanent_exit',
    'error_planning',
    'error_unknown',
    'success',
    'success_drop_off',
  ]).isRequired,
  source: PropTypes.string,
  type: PropTypes.oneOf([
    'access',
    'on_demand',
    'trigger_door',
  ]).isRequired,
  usage: PropTypes.oneOf([
    'input',
    'na',
    'output',
  ]),
});

/*
** Customers
*/
export const cpmCustomerDataShape = PropTypes.shape({
  comments: PropTypes.string,
  parkingCategory: PropTypes.shape({ id: PropTypes.number.isRequired }),
  email: PropTypes.string,
  firstname: PropTypes.string,
  id: PropTypes.number.isRequired,
  lastname: PropTypes.string.isRequired,
  phone: PropTypes.string,
  spaceNumber: PropTypes.string,
  startedParkings: PropTypes.arrayOf(
    PropTypes.shape({
      begin: PropTypes.string.isRequired,
      end: PropTypes.string,
      id: PropTypes.number.isRequired,
      parkingCategory: PropTypes.shape({ id: PropTypes.number.isRequired }),
      reference: PropTypes.string.isRequired,
      spacesQuantity: PropTypes.number.isRequired,
    }),
  ),
});

/*
** Parkings
*/
export const cpmPaymentShape = PropTypes.shape({
  amount: PropTypes.number.isRequired,
  date: PropTypes.string.isRequired,
  provider: PropTypes.oneOf(['external', 'free', 'stripe']),
  refundedAmount: PropTypes.number,
  status: PropTypes.oneOf(['cancelled', 'in_progress', 'refunded', 'succeeded']).isRequired,
  stripeDetails: PropTypes.shape({
    clientSecret: PropTypes.string.isRequired,
  }),
  target: PropTypes.oneOf(['terminal', 'web']).isRequired,
});

export const cpmPaymentErrorShape = PropTypes.shape({
  code: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
});

export const cpmPurchaseOfferShape = PropTypes.shape({
  amountExclTax: PropTypes.number.isRequired,
  amountInclTax: PropTypes.number.isRequired,
  begin: PropTypes.string.isRequired,
  customer: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }),
  discount: PropTypes.number.isRequired,
  discountedAmountExclTax: PropTypes.number.isRequired,
  discountedAmountInclTax: PropTypes.number.isRequired,
  end: PropTypes.string.isRequired,
  expiresAt: PropTypes.string.isRequired,
  parking: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }),
  parkingCategory: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }),
  refundReason: PropTypes.oneOf([
    'billing_error',
    'cancellation',
    'exceptional_case',
    'goodwill_gesture',
    'technical_issue',
    'unavailable_space']),
  priceRule: cpmPriceRuleDataShape.isRequired,
  priceType: PropTypes.oneOf(['free', 'paying']).isRequired,
  reference: PropTypes.string.isRequired,
  selfCheckoutType: PropTypes.oneOf(['definitive_exit', 'temporary_exit']),
  serviceFeeAmountExclTax: PropTypes.number,
  serviceFeeAmountInclTax: PropTypes.number,
  spacesQuantity: PropTypes.number.isRequired,
  status: PropTypes.oneOf(['expired', 'progress', 'used']).isRequired,
  totalAmountExclTax: PropTypes.number.isRequired,
  totalAmountInclTax: PropTypes.number.isRequired,
  type: PropTypes.oneOf(['booking', 'booking_extension', 'booking_overstay', 'on_demand_definitive_exit', 'on_demand_temporary_exit', 'on_demand_extension', 'on_demand_overstay']).isRequired,
});

export const cpmPurchaseShape = PropTypes.shape({
  amountExclTax: PropTypes.number.isRequired,
  amountInclTax: PropTypes.number.isRequired,
  discount: PropTypes.number.isRequired,
  discountedAmountExclTax: PropTypes.number.isRequired,
  discountedAmountInclTax: PropTypes.number.isRequired,
  id: PropTypes.number.isRequired,
  offers: PropTypes.arrayOf(cpmPurchaseOfferShape).isRequired,
  paidAmount: PropTypes.number.isRequired,
  paymentStatus: PropTypes.oneOf(['cancelled', 'error', 'paid', 'refunded', 'required']).isRequired,
  payments: PropTypes.arrayOf(cpmPaymentShape).isRequired,
  priceRule: cpmPriceRuleDataShape.isRequired,
  serviceFeeAmountExclTax: PropTypes.number,
  serviceFeeAmountInclTax: PropTypes.number,
  totalAmountExclTax: PropTypes.number.isRequired,
  totalAmountInclTax: PropTypes.number.isRequired,
  type: PropTypes.oneOf(['free', 'paying']).isRequired,
});

export const cpmParkingDataShape = PropTypes.shape({
  begin: PropTypes.string,
  commnents: PropTypes.string,
  customer: cpmCustomerDataShape,
  doors: PropTypes.arrayOf(cpmDoorDataShape),
  end: PropTypes.string,
  id: PropTypes.number.isRequired,
  keypadCodes: PropTypes.arrayOf(PropTypes.string),
  parkingCategory: PropTypes.shape({ id: PropTypes.number.isRequired }),
  planning: PropTypes.shape({ id: PropTypes.number.isRequired }),
  purchases: PropTypes.arrayOf(cpmPurchaseShape).isRequired,
  reference: PropTypes.string.isRequired,
  restrictedByAntipassbackEntrance: PropTypes.bool,
  restrictedByAntipassbackExit: PropTypes.bool,
  restrictedByForbiddenEntry: PropTypes.bool,
  restrictedByForbiddenExit: PropTypes.bool,
  restrictedByPermanentExit: PropTypes.bool,
  spacesQuantity: PropTypes.number.isRequired,
  state: PropTypes.shape({
    insideQuantity: PropTypes.number.isRequired,
    outsideQuantity: PropTypes.number.isRequired,
    outsideQuantityPermanentExit: PropTypes.number.isRequired,
    unknownQuantity: PropTypes.number.isRequired,
  }),
  status: PropTypes.oneOf(['created', 'finished', 'started', 'in_overstay', 'in_progress', 'cancelled']).isRequired,
  synchronizationStatus: cpmSynchronizationStatusShape,
  type: PropTypes.oneOf(['assigned', 'booking', 'regular']).isRequired,
  vehicles: PropTypes.arrayOf(
    PropTypes.shape({
      licensePlate: PropTypes.string.isRequired,
    }),
  ),
});

/*
** Metrics
*/
export const cpmMetricOccupancyLiveShape = PropTypes.shape({
  global: PropTypes.shape({
    rate: PropTypes.number.isRequired,
  }),
  parkingCategories: PropTypes.arrayOf(
    PropTypes.shape({
      parkingCategoryId: PropTypes.number.isRequired,
      rate: PropTypes.number.isRequired,
    }),
  ).isRequired,
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

export const cpmMetricOccupancyCountShape = PropTypes.shape({
  average: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
});

export const cpmMetricOccupancyPeriodDataShape = PropTypes.shape({
  date: PropTypes.string.isRequired,
  rate: PropTypes.shape({
    max: PropTypes.number.isRequired,
    min: PropTypes.number.isRequired,
    average: PropTypes.number.isRequired,
  }).isRequired,
  count: cpmMetricOccupancyCountShape,
});

export const cpmMetricOccupancyPeriodShape = PropTypes.shape({
  global: PropTypes.shape({
    periods: PropTypes.arrayOf(cpmMetricOccupancyPeriodDataShape).isRequired,
  }),
  parkingCategories: PropTypes.arrayOf(
    PropTypes.shape({
      parkingCategoryId: PropTypes.number.isRequired,
      periods: PropTypes.arrayOf(cpmMetricOccupancyPeriodDataShape).isRequired,
    }),
  ).isRequired,
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

export const cpmMetricAccessesDataShape = PropTypes.shape({
  date: PropTypes.string.isRequired,
  quantities: PropTypes.shape({
    input: PropTypes.number.isRequired,
    output: PropTypes.number.isRequired,
    na: PropTypes.number.isRequired,
  }).isRequired,
});

export const cpmMetricAccessesShape = PropTypes.shape({
  global: PropTypes.shape({
    periods: PropTypes.arrayOf(cpmMetricAccessesDataShape).isRequired,
  }),
  parkingCategories: PropTypes.arrayOf(
    PropTypes.shape({
      parkingCategoryId: PropTypes.number.isRequired,
      periods: PropTypes.arrayOf(cpmMetricAccessesDataShape).isRequired,
    }),
  ).isRequired,
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

export const cpmMetricRevenuesDataShape = PropTypes.shape({
  date: PropTypes.string.isRequired,
  value: PropTypes.shape({
    amountInclTax: PropTypes.number.isRequired,
    amountExclTax: PropTypes.number.isRequired,
  }).isRequired,
});

export const cpmMetricRevenuesShape = PropTypes.shape({
  global: PropTypes.shape({
    periods: PropTypes.arrayOf(cpmMetricRevenuesDataShape).isRequired,
  }),
  parkingCategories: PropTypes.arrayOf(
    PropTypes.shape({
      parkingCategoryId: PropTypes.number.isRequired,
      periods: PropTypes.arrayOf(cpmMetricRevenuesDataShape).isRequired,
    }),
  ).isRequired,
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});

/*
** Carpark Booking
*/
export const cpmCarparkBookingOptionsShape = PropTypes.shape({
  customers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      email: PropTypes.string,
      firstname: PropTypes.string,
      lastname: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  parkingCategory: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  priceGrid: cpmBookingPriceGridDataShape,
});

export const cpmBookingDataShape = PropTypes.shape({
  begin: PropTypes.string.isRequired,
  carpark: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    publicName: PropTypes.string,
    timezone: PropTypes.string,
  }).isRequired,
  customer: cpmCustomerDataShape,
  end: PropTypes.string.isRequired,
  keypadCodes: PropTypes.arrayOf(PropTypes.string).isRequired,
  parkingCategory: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }),
  purchases: PropTypes.arrayOf(cpmPurchaseShape).isRequired,
  reference: PropTypes.string.isRequired,
  spacesQuantity: PropTypes.number.isRequired,
  status: PropTypes.oneOf(['created', 'finished', 'started', 'in_overstay', 'in_progress', 'cancelled']).isRequired,
  vehicles: PropTypes.arrayOf(
    PropTypes.shape({
      licensePlate: PropTypes.string.isRequired,
    }),
  ),
});

export const cpmMyBookingShape = PropTypes.shape({
  begin: PropTypes.string.isRequired,
  carpark: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    timezone: PropTypes.string,
  }).isRequired,
  end: PropTypes.string.isRequired,
  keypadCodes: PropTypes.arrayOf(PropTypes.string).isRequired,
  purchases: PropTypes.arrayOf(cpmPurchaseShape).isRequired,
  reference: PropTypes.string.isRequired,
  spacesQuantity: PropTypes.number.isRequired,
  status: PropTypes.oneOf(['created', 'finished', 'started', 'in_overstay', 'in_progress', 'cancelled']).isRequired,
  vehicles: PropTypes.arrayOf(
    PropTypes.shape({
      licensePlate: PropTypes.string.isRequired,
    }),
  ),
});

export const cpmMyBookingPurchasesShape = PropTypes.shape({
  purchases: PropTypes.arrayOf(cpmPurchaseShape).isRequired,
  reference: PropTypes.string.isRequired,
});

/*
** Onepark Booking
*/
export const cpmOneparkBookingDataShape = PropTypes.shape({
  begin: PropTypes.string,
  bookingId: PropTypes.string.isRequired,
  bookingUsage: PropTypes.oneOf(['b2b_subscription', 'package', 'subscription']).isRequired,
  customerFirstname: PropTypes.string,
  customerLastname: PropTypes.string.isRequired,
  end: PropTypes.string,
  keypadCode: PropTypes.string,
  status: PropTypes.oneOf(['created', 'finished', 'started']),
  licensePlate: PropTypes.string,
});

/*
** Account
*/
const cpmAccountHistoryShape = PropTypes.shape({
  cause: PropTypes.oneOf(['bad_affiliate_site', 'end_of_lease', 'fraud', 'invalid_subscription']),
  comments: PropTypes.string,
  date: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['active', 'inactive', 'pending']).isRequired,
});
export const cpmAccountShape = PropTypes.shape({
  authorizations: PropTypes.arrayOf(
    PropTypes.shape({
      carpark: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string,
      }),
      cause: PropTypes.oneOf(['bad_affiliate_site', 'end_of_lease', 'fraud', 'invalid_subscription']),
      company: PropTypes.string,
      history: PropTypes.arrayOf(cpmAccountHistoryShape).isRequired,
      id: PropTypes.number.isRequired,
      status: PropTypes.oneOf(['active', 'inactive', 'pending']).isRequired,
    }).isRequired,
  ).isRequired,
  email: PropTypes.string.isRequired,
  firstname: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  history: PropTypes.arrayOf(cpmAccountHistoryShape).isRequired,
  lastname: PropTypes.string.isRequired,
});

/*
** OnDemand Access
*/
export const cpmOnDemandAccessForCheckoutDataShape = PropTypes.shape({
  active: PropTypes.bool.isRequired,
  begin: PropTypes.string.isRequired,
  email: PropTypes.string,
  end: PropTypes.string,
  keypadCode: PropTypes.string,
  id: PropTypes.number.isRequired,
  licensePlate: PropTypes.string.isRequired,
  priceGrid: cpmPriceGridDataShape,
  purchases: PropTypes.arrayOf(cpmPurchaseShape),
  reference: PropTypes.string.isRequired,
  selfCheckoutType: PropTypes.oneOf(['definitive_exit', 'temporary_exit']),
});

export const cpmOnDemandAccessDataShape = PropTypes.shape({
  active: PropTypes.bool.isRequired,
  begin: PropTypes.string.isRequired,
  customer: PropTypes.shape({
    id: PropTypes.number.isRequired,
    parkingCategory: PropTypes.shape({
      id: PropTypes.number.isRequired,
      type: PropTypes.oneOf(['on_demand', 'regular']).isRequired,
    }),
  }),
  email: PropTypes.string,
  end: PropTypes.string,
  keypadCode: PropTypes.string,
  id: PropTypes.number.isRequired,
  licensePlate: PropTypes.string.isRequired,
  priceGrid: cpmPriceGridDataShape,
  purchases: PropTypes.arrayOf(cpmPurchaseShape),
  readlicensePlate: PropTypes.string,
  reference: PropTypes.string.isRequired,
  restrictedByAntipassbackEntrance: PropTypes.bool,
  restrictedByAntipassbackExit: PropTypes.bool,
  restrictedByForbiddenEntry: PropTypes.bool,
  restrictedByForbiddenExit: PropTypes.bool,
  restrictedByPermanentExit: PropTypes.bool,
  selfCheckoutType: PropTypes.oneOf(['definitive_exit', 'temporary_exit']),
  spacesQuantity: PropTypes.number.isRequired,
  state: PropTypes.shape({
    insideQuantity: PropTypes.number.isRequired,
    outsideQuantity: PropTypes.number.isRequired,
    outsideQuantityPermanentExit: PropTypes.number.isRequired,
    unknownQuantity: PropTypes.number.isRequired,
  }),
  synchronizationStatus: cpmSynchronizationStatusShape,
});

/*
** Carpark Invoice
*/
export const cpmInvoiceDataShape = PropTypes.shape({
  amountExclTax: PropTypes.number.isRequired,
  amountInclTax: PropTypes.number.isRequired,
  canBeUpdated: PropTypes.bool,
  customerEmail: PropTypes.string,
  customerFirstname: PropTypes.string,
  customerLastname: PropTypes.string,
  date: PropTypes.string.isRequired,
  number: PropTypes.string.isRequired,
  priceRule: cpmPriceRuleDataShape.isRequired,
  serviceFeeAmountInclTax: PropTypes.number,
  taxAmount: PropTypes.number.isRequired,
  type: PropTypes.oneOf(['payment', 'refund']).isRequired,
});
/*
** Partner
*/
export const cpmPartnersDataShape = PropTypes.shape({
  financialInformation: PropTypes.shape({
    address: addressShape.isRequired,
    emailForCommissionInvoices: PropTypes.string.isRequired,
    europeanVatNumber: PropTypes.string.isRequired,
    iban: PropTypes.string.isRequired,
    invoiceIdentifier: PropTypes.string.isRequired,
    nationalIdentifier: PropTypes.string.isRequired,
  }),
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
});
/*
** Workspace
*/
export const cpmWorkspaceDataShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
});
export const cpmWorkspaceDataShowShape = PropTypes.shape({
  result: cpmWorkspaceDataShape,
  error: errorShape,
  loading: PropTypes.bool.isRequired,
});
export const cpmWorkspaceGroupShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  workspace: cpmWorkspaceDataShape,
});
export const cpmWorkspaceGroupForSlugShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
});

// Service fee rule
export const cpmServiceFeeRuleShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  maximumAmount: PropTypes.number,
  minimumAmount: PropTypes.number,
  name: PropTypes.string.isRequired,
  priceThreshold: PropTypes.number,
  value: PropTypes.number.isRequired,
});

// Slug
export const cpmSlugCustomizationShape = PropTypes.shape({
  hideHeaderLogo: PropTypes.bool,
  logo: PropTypes.string,
  showOnDemandCheckoutTutorial: PropTypes.bool,
  title: PropTypes.string,
});
export const cpmSlugShape = PropTypes.shape({
  comments: PropTypes.string,
  customization: cpmSlugCustomizationShape,
  domain: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  slug: PropTypes.string,
  type: PropTypes.string,
  usages: PropTypes.arrayOf(PropTypes.string).isRequired,
  usedOnPrintedQrCode: PropTypes.bool,
  workspace: cpmWorkspaceDataShape,
  workspaceGroup: cpmWorkspaceGroupForSlugShape,
});
export const cpmCurrentSlugClientShape = PropTypes.shape({
  domain: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  slug: PropTypes.string,
});
export const cpmCurrentSlugShape = PropTypes.shape({
  error: errorShape,
  loading: PropTypes.bool.isRequired,
  result: cpmCurrentSlugClientShape,
});



// WEBPACK FOOTER //
// ./constants/PropTypesShapes.js